import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Table, type Column } from '@/components/ui/Table';
import { cn } from '@/lib/utils';
import type { TransportVehicle } from '@/types/transport';
import {
  VEHICLE_STATUS,
  expiryTone,
  serviceTone,
  toDateLabel,
  toRelativeDayLabel,
} from '../constants';
import { ROWS_PER_PAGE, useRegister } from '../useRegister';
import { RegisterToolbar } from './RegisterToolbar';
import { RowActions } from './RowActions';
import { VehicleCard } from './VehicleCard';

/** What a status in the address may be — every one this register's rows can hold. */
const VEHICLE_STATUSES = Object.keys(VEHICLE_STATUS);

/** How many vehicles are drawn as cards before the list below takes over. */
const CARDS_SHOWN = 4;

export interface VehiclesTabProps {
  vehicles: TransportVehicle[];
  onOpenVehicle?: (vehicle: TransportVehicle) => void;
  onEditVehicle?: (vehicle: TransportVehicle) => void;
  onVehicleMenu?: (vehicle: TransportVehicle) => void;
}

/**
 * The fleet — the first few as cards, then all of them as a list.
 *
 * The same shape as the Routes tab, and deliberately so: an office moving
 * between the two tabs should not have to learn a second screen. The cards are
 * the first of the same filtered set the list pages through, not a separate
 * selection.
 */
export function VehiclesTab({
  vehicles,
  onOpenVehicle,
  onEditVehicle,
  onVehicleMenu,
}: VehiclesTabProps) {
  const register = useRegister({
    prefix: 'vehicles',
    statuses: VEHICLE_STATUSES,
    rows: vehicles,
    fieldsOf: (vehicle) => [
      vehicle.registrationNumber,
      vehicle.model,
      vehicle.driverName,
      vehicle.supporterName,
      vehicle.routeName,
    ],
    statusOf: (vehicle) => vehicle.status,
  });

  const columns: Column<TransportVehicle>[] = [
    {
      key: 'number',
      header: 'Vehicle no.',
      render: (vehicle) => <span className="font-bold text-ink">{vehicle.registrationNumber}</span>,
    },
    {
      key: 'kind',
      header: 'Type',
      render: (vehicle) => (
        <span className="font-semibold text-ink-2">{vehicle.kind === 'BUS' ? 'Bus' : 'Van'}</span>
      ),
    },
    {
      key: 'capacity',
      header: 'Capacity',
      align: 'center',
      render: (vehicle) => <span className="font-semibold text-ink-2">{vehicle.capacity}</span>,
    },
    {
      key: 'driver',
      header: 'Driver',
      hideOnMobile: true,
      render: (vehicle) => <PersonCell name={vehicle.driverName} mobile={vehicle.driverMobile} />,
    },
    {
      key: 'supporter',
      header: 'Supporter',
      hideOnMobile: true,
      render: (vehicle) => (
        <PersonCell name={vehicle.supporterName} mobile={vehicle.supporterMobile} />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (vehicle) => (
        <Badge tone={VEHICLE_STATUS[vehicle.status].tone} withDot>
          {VEHICLE_STATUS[vehicle.status].label}
        </Badge>
      ),
    },
    {
      key: 'route',
      header: 'Assigned route',
      hideOnMobile: true,
      render: (vehicle) =>
        vehicle.routeName ? (
          <span className="font-semibold text-ink-2">
            {vehicle.routeCode ? `${vehicle.routeCode} — ` : ''}
            {vehicle.routeName}
          </span>
        ) : (
          <span className="font-semibold text-ink-3">—</span>
        ),
    },
    {
      key: 'fitness',
      header: 'Fitness expiry',
      hideOnMobile: true,
      render: (vehicle) => <DatedCell iso={vehicle.fitnessExpiry} mode="expiry" />,
    },
    {
      key: 'service',
      header: 'Last service',
      hideOnMobile: true,
      render: (vehicle) => <DatedCell iso={vehicle.lastServicedOn} mode="since" />,
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      render: (vehicle) => (
        <RowActions
          subject={vehicle.registrationNumber}
          onOpen={onOpenVehicle && (() => onOpenVehicle(vehicle))}
          onEdit={onEditVehicle && (() => onEditVehicle(vehicle))}
          onMore={onVehicleMenu && (() => onVehicleMenu(vehicle))}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-4">
        <RegisterToolbar
          title="All vehicles"
          searchPlaceholder="Search vehicles…"
          register={register}
          statusOptions={[
            { value: 'ON_ROUTE', label: VEHICLE_STATUS.ON_ROUTE.label },
            { value: 'MAINTENANCE', label: VEHICLE_STATUS.MAINTENANCE.label },
            { value: 'INACTIVE', label: VEHICLE_STATUS.INACTIVE.label },
          ]}
        />

        {register.visible.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {register.visible.slice(0, CARDS_SHOWN).map((vehicle, index) => (
              <div
                key={vehicle.uuid}
                className="animate-fade-up"
                style={{ animationDelay: `${Math.min(index, 4) * 60}ms` }}
              >
                <VehicleCard vehicle={vehicle} onMenu={onVehicleMenu} />
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-line bg-surface p-4 shadow-sm">
            <EmptyState
              title="No vehicles match"
              description="Nothing matches that search or filter. Try another number, driver or status."
            />
          </div>
        )}
      </section>

      <section className="rounded-3xl border border-line bg-surface shadow-sm">
        <header className="border-b border-line px-4 py-3.5">
          <h2 className="text-base font-bold text-ink">Vehicles list</h2>
          <p className="mt-0.5 text-sm text-ink-2">
            The whole fleet, with its papers, its people and its last service.
          </p>
        </header>

        <Table
          columns={columns}
          rows={register.page}
          rowKey={(vehicle) => vehicle.uuid}
          variant="plain"
          empty={
            <EmptyState
              title="No vehicles"
              description="Vehicles appear here once the fleet has been set up."
            />
          }
        />

        {register.visible.length > 0 && (
          <Pagination
            meta={register.meta}
            onPageChange={register.goToPage}
            itemLabel="vehicles"
            showPageNumbers
            limitOptions={ROWS_PER_PAGE}
            onLimitChange={register.setLimit}
          />
        )}
      </section>
    </div>
  );
}

/** A name over the number to reach them on. */
function PersonCell({ name, mobile }: { name: string | null; mobile: string | null }) {
  if (!name) return <span className="font-semibold text-ink-3">Not assigned</span>;

  return (
    <div className="min-w-0">
      <p className="truncate font-bold text-ink">{name}</p>
      {mobile && <p className="mt-0.5 truncate text-xs font-semibold text-ink-3">{mobile}</p>}
    </div>
  );
}

/**
 * A date over how it reads against today.
 *
 * The relative line is the one that gets acted on — "103 days left" is a
 * decision and "25 Aug 2026" is a lookup — so it carries the colour, and a
 * certificate inside a month of expiry says so in amber.
 *
 * `mode` decides what the colour means, because the two dates in this table ask
 * opposite questions: a fitness certificate in the past is a vehicle that may
 * not carry children, while a service in the past is simply the last one.
 */
function DatedCell({ iso, mode }: { iso: string; mode: 'expiry' | 'since' }) {
  const tone = mode === 'expiry' ? expiryTone(iso) : serviceTone(iso);

  return (
    <div className="min-w-0">
      <p className="truncate text-xs font-semibold text-ink-2">{toDateLabel(iso)}</p>
      <p
        className={cn(
          'mt-0.5 truncate text-xs font-bold',
          tone === 'crit' ? 'text-crit' : tone === 'warn' ? 'text-warn' : 'text-ok',
        )}
      >
        {toRelativeDayLabel(iso)}
      </p>
    </div>
  );
}
